import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { ActionAPI, AutocompleteAPI, ContextualBarAPI, FetchAPI, FieldAPI, InputAPI, Link, Panel, PanelAPI } from '@philips-emr/tws-components';
import { Size, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { generateUUID, isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { NegotiationContractualDraftAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

enum AmendmentType {
  CHANGED = 'A',
  EXCLUDED = 'E',
  INCLUDED = 'I',
  ORIGINAL = 'O',
}

interface ContractualDraft {
  draftTitle: string;
  nrSeqItemProposal: number;
  firstLevel: number;
  secondLevel: number;
  thirdLevel: number;
  fourthLevel: number;
  ieValueCounterProposal: string;
  edit: string;
  counterOffer: string;
  dsFile: string;
  proposalMenu: string;
  attachmentsMenu: string;
  nrSeqContractClause: number;
  ieAdditionType: AmendmentType;
  additionType: string;
  vlDominio: AmendmentType;
  dsValorDominio: string;
}

@Controller({ fragment: FragmentNames.NEGOTIATION_CONTRACTUAL_DRAFT })
class NegotiationContractualDraftController extends BaseController {

  @Inject(Aliases.FETCH_CLAUSE)
  private fetchClause: FetchAPI;
  @Inject(Aliases.CONTEXTUAL_BAR_CLAUSE)
  private contextualBarClause: ContextualBarAPI;
  @Inject(Aliases.PANEL_CLAUSE)
  private panelClause: PanelAPI;
  @Inject(Aliases.PANEL_PROPOSAL)
  private panelProposal: PanelAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.PANEL_ATTACHMENTS)
  private panelAttachments: PanelAPI;
  @Inject(Aliases.FETCH_CLAUSES)
  private fetchClauses: FetchAPI;
  @Inject(Aliases.PANEL_REQUEST_CHANGE)
  private panelRequestChange: PanelAPI;
  @Inject(Aliases.ACTION_REQUEST_CHANGE)
  private actionRequestChange: ActionAPI;
  @Inject(Aliases.FIELD_COUNTER_OFFER)
  private fieldCounterOffer: FieldAPI;
  @Inject(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  private autocompleteMenuMobile: AutocompleteAPI;

  @OnMount(Aliases.FETCH_CLAUSES)
  onMountFetchClauses(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal });
    api.forceFetch();
  }

  @OnMount(Aliases.FETCH_MENU_MOBILE)
  onMountFetchMenuMobile(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, mobile: true });
    api.forceFetch();
  }

  @OnDynamicContentRender(Aliases.FIELD_CLAUSES)
  onRenderClauses(event: RenderEvent): void {
    const { data, render } = event;
    if (!isEmptyArrayOrObject(data)) {
      const { draftTitle, nrSeqItemProposal, secondLevel, thirdLevel, fourthLevel } = data as ContractualDraft;
      const margin = chooseMargin(secondLevel, thirdLevel, fourthLevel);
      render((): ReactElement =>
        <Panel className="" uuid={generateUUID()} margin={margin}>
          <Link value={draftTitle} onClick={() => this.forceFetchClause(nrSeqItemProposal, draftTitle)} size={Size.MEDIUM} />
        </Panel>);
    }
  }

  @OnClick(Aliases.LINK_PROPOSAL)
  onClickProposal(): void {
    this.panelProposal.setVisible(true);
    this.panelClause.setVisible(false);
    this.panelAttachments.setVisible(false);
  }

  @OnClick(Aliases.LINK_ATTACHMENT)
  onClickAttachment(): void {
    this.panelProposal.setVisible(false);
    this.panelClause.setVisible(false);
    this.panelAttachments.setVisible(true);
  }

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(event: MountEvent): void {
    const api = event.target as InputAPI;
    api.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<ContractualDraft>): void {
    const params = this.fetchClauses.getParameters();
    const draftTitle = event.value;
    this.fetchClauses.setParameters({ ...params, draftTitle });
    this.fetchClauses.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_CLAUSE)
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as ContractualDraft;
    if (data) {
      const { edit, ieValueCounterProposal } = data;
      this.actionRequestChange.setEnabled(edit == 'S');
      this.fieldCounterOffer.setVisible(ieValueCounterProposal == 'S');

    }
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<ContractualDraft>): void {
    const { nrSeqItemProposal, draftTitle, proposalMenu, attachmentsMenu } = event.value;
    if (proposalMenu) {
      this.panelProposal.setVisible(true);
      this.panelClause.setVisible(false);
      this.panelAttachments.setVisible(false);
      return;
    }
    if (attachmentsMenu) {
      this.panelProposal.setVisible(false);
      this.panelClause.setVisible(false);
      this.panelAttachments.setVisible(true);
      return;
    }
    this.forceFetchClause(nrSeqItemProposal, draftTitle);
  }

  private forceFetchClause(nrSeqItemProposal: number, draftTitle: string): void {
    const visible = this.panelClause.isVisible();
    this.panelLoader.setVisible(!visible);
    this.panelClause.setVisible(true);
    this.panelProposal.setVisible(false);
    this.panelAttachments.setVisible(false);
    this.context.set({ nrSeqItemProposal, draftTitle });
    setTimeout(() => {
      const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
      this.fetchClause.setParameters({ nrSeqNegotiation, nrSeqProposal, nrSeqItemProposal });
      this.fetchClause.forceFetch();
      this.contextualBarClause.setTitle(draftTitle);
      this.panelLoader.setVisible(false);
    }, visible ? 0 : 500);

  }

  @OnClick(Aliases.ACTION_REQUEST_CHANGE)
  onClickAccept(): void {
    this.panelRequestChange.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_REQUEST_CHANGE)
  onAcceptProposal(event: SubmitEvent): void {
    const { counterOffer } = event.record as ContractualDraft;
    const { nrSeqItemProposal, draftTitle } = this.context.getBulkData();
    const selectedItens = [nrSeqItemProposal];

    const body = { counterOffer, selectedItens };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_CONTRACT_DRAFT, { nrSeqProposal, nrSeqNegotiation, counterOffer }, { body }).then(() => {
      ToastService.open({ message: 'provider.request-sucessfull', level: ToastLevel.SUCCESS });
      this.forceFetchClause(nrSeqItemProposal as number, draftTitle as string);
      this.panelRequestChange.setVisible(false);
    }).catch((error) => {
      this.panelRequestChange.setVisible(false);
      ToastService.open(error.body);
    });
  }

  @OnClose(Aliases.MODAL_REQUEST_CHANGE)
  onCloseModalAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnCancel(Aliases.FORM_REQUEST_CHANGE)
  onCancelFormAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnAfterFetch(Aliases.FETCH_MENU_MOBILE)
  onAfterFetchMobile(event: FetchEvent): void {
    if (event.data && !isEmptyArrayOrObject(event.data)) {
      const data = event.data as [{}];
      this.autocompleteMenuMobile.setValue(data[0]);
    }
  }

}

function chooseMargin(secondLevel: number, thirdLevel: number, fourthLevel: number): string {
  if (fourthLevel > 0) {
    return '0.4rem 0 0 3rem';
  }

  if (thirdLevel > 0) {
    return '0.4rem 0 0 2rem';
  }

  if (secondLevel > 0) {
    return '0.4rem 0 0 1rem';
  }

  return '0, 0, 0, 0';
}

export {
  NegotiationContractualDraftController,
  ContractualDraft,
  chooseMargin,
  AmendmentType,
};
